import useToast from "@repo/core/components/core/Toast";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { apiInstance } from "@repo/core/services/instances/api";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { useCreditProposal } from "@repo/core/screens/ProposalEffectiveFlows/Credit/hooks/useCreditProposal";
import { isUnicoDocumentType } from "@repo/core/enums/proposals";
import { useCallback } from "react";
import { usePreProposalStore } from "../../store/preProposalStore";
import { persistPreProposalAlterarDataConferencePendingOrThrow } from "../../services/persistPreProposalAlterarPending.service";
import {
  mandatoryDocumentsIncludeBiometry,
  mergeRequiredDocumentsWithUploadedStatus
} from "../../screens/BiometryValidation/utils";
import { navigateToReanalysisCreditFlowIfApplicable } from "../../screens/BiometryValidation/services/navigation.service";
import {
  ensureRequiredBiometryDocument,
  filterBiometryUploadedDocumentTypes
} from "../../services/proposalEditBiometry.service";

const proposalsResource = new ProposalsResource(apiInstance);

interface ResolveRequiredDocumentsAfterSecurityOptions {
  beforeNavigate?: () => void;
}

export const useResolveRequiredDocumentsAfterSecurity = () => {
  const { push, resetTo } = useNavigation();
  const { clearCreditProposal } = useCreditProposal();
  const proposalData = usePreProposalStore((state) => state.proposalData);
  const setRequiredDocuments = usePreProposalStore(
    (state) => state.setRequiredDocuments
  );
  const setProposalData = usePreProposalStore((state) => state.setProposalData);
  const { error } = useToast();

  const resolveRequiredDocumentsAfterSecurity = useCallback(
    async (options?: ResolveRequiredDocumentsAfterSecurityOptions) => {
      let hasPersistedCurrentProposal = false;

      try {
        const latestProposalData = usePreProposalStore.getState().proposalData;

        if (latestProposalData?.lngProposta) {
          await persistPreProposalAlterarDataConferencePendingOrThrow(
            latestProposalData
          );
        }
        hasPersistedCurrentProposal = true;

        const { data: requiredDocuments } =
          await proposalsResource.validateRequiredDocuments(
            latestProposalData?.strProduto || proposalData?.strProduto || ""
          );

        const shouldForceNewBiometry =
          latestProposalData?.shouldForceNewBiometry === true;
        const uploadedProposalDocumentTypes =
          latestProposalData?.uploadedProposalDocumentTypes ||
          proposalData?.uploadedProposalDocumentTypes ||
          [];
        const effectiveUploadedProposalDocumentTypes = shouldForceNewBiometry
          ? filterBiometryUploadedDocumentTypes(uploadedProposalDocumentTypes)
          : uploadedProposalDocumentTypes;
        const enrichedRequiredDocuments =
          mergeRequiredDocumentsWithUploadedStatus(
            requiredDocuments || [],
            effectiveUploadedProposalDocumentTypes
          );
        const effectiveRequiredDocuments = shouldForceNewBiometry
          ? ensureRequiredBiometryDocument(enrichedRequiredDocuments)
          : enrichedRequiredDocuments;

        setRequiredDocuments(effectiveRequiredDocuments);

        if (shouldForceNewBiometry) {
          setProposalData(
            {
              uploadedProposalDocumentTypes:
                effectiveUploadedProposalDocumentTypes
            },
            { skipAlterarSync: true }
          );
        }

        if (!effectiveRequiredDocuments.length) {
          options?.beforeNavigate?.();

          if (
            navigateToReanalysisCreditFlowIfApplicable(
              latestProposalData || proposalData,
              resetTo,
              clearCreditProposal
            )
          ) {
            return;
          }
          push(ROUTES.preProposalsVerifyInformations);
          return;
        }

        if (mandatoryDocumentsIncludeBiometry(effectiveRequiredDocuments)) {
          options?.beforeNavigate?.();
          push(ROUTES.preProposalsBiometryValidation);
          return;
        }

        const hasManualIdentityDocument = effectiveRequiredDocuments.some(
          (document) =>
            isUnicoDocumentType(String(document.strTipoDocumento).trim())
        );

        options?.beforeNavigate?.();
        push(ROUTES.preProposalsSelectDocuments, {
          ...(hasManualIdentityDocument && { forceManualCapture: true })
        });
      } catch (err) {
        console.error("Error resolving required documents:", err);

        if (!hasPersistedCurrentProposal) {
          error("Erro ao salvar a proposta atualizada. Tente novamente.");
          return;
        }

        options?.beforeNavigate?.();
        push(ROUTES.preProposalsSelectDocuments, {
          forceManualCapture: true
        });
      }
    },
    [
      proposalData,
      push,
      resetTo,
      clearCreditProposal,
      setRequiredDocuments,
      setProposalData,
      error
    ]
  );

  return { resolveRequiredDocumentsAfterSecurity };
};
